import { Trophy } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompetitionDashboardCard } from './CompetitionDashboardCard';
import { PublicCompetitionCard } from './PublicCompetitionCard';
import { EnrolledCompetition } from '../hooks/useUserCompetitionEnrollment';
import { Competition } from '../types';

import { useCompetitionEntries } from '../hooks/useCompetitionEntries';

interface MyCompetitionsDashboardProps {
  enrolledCompetitions: EnrolledCompetition[];
  availableCompetitions: Competition[];
  loading: boolean;
  organizationSlug: string;
  organizationName: string;
  publicStats?: Record<string, { total: number; remaining: number | null }>;
  wcFirstKickoff?: Date | null;
  firstGameweekDeadlines?: Record<string, Date>;
}

export function MyCompetitionsDashboard({
  enrolledCompetitions,
  availableCompetitions,
  loading,
  organizationSlug,
  organizationName,
  publicStats = {},
  wcFirstKickoff = null,
  firstGameweekDeadlines = {},
}: MyCompetitionsDashboardProps) {
  const navigate = useNavigate();
  const { updateEntryNickname } = useCompetitionEntries();

  const handleUpdateEntryNickname = useCallback(async (entryId: string, newNickname: string, competitionId: string) => {
    return updateEntryNickname(entryId, newNickname, competitionId);
  }, [updateEntryNickname]);

  if (loading) {
    return (
      <div className="space-y-3" aria-label="Loading your competitions">
        <div className="h-6 w-48 skeleton-shimmer bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        {[0, 1].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 border-2 border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 skeleton-shimmer bg-gray-200 dark:bg-gray-700 rounded-xl flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-5 w-2/3 skeleton-shimmer bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-3 w-1/3 skeleton-shimmer bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
            <div className="h-9 w-full skeleton-shimmer bg-gray-200 dark:bg-gray-700 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  const handleNavigate = (competition: EnrolledCompetition | Competition) => {
    const typeMap: Record<string, string> = { lms: 'lms', predictor: 'predictor', worldcup: 'worldcup' };
    const segment = typeMap[competition.competitionType || 'lms'] || 'lms';
    const path = `/${organizationSlug}/${segment}?comp=${competition.id}`;
    navigate(path);
  };




  // No competitions at all (neither enrolled nor available)
  if (enrolledCompetitions.length === 0 && availableCompetitions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-2xl shadow-lg mb-4">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Welcome to {organizationName}
          </h2>
        </div>
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl mb-4">
          <Trophy className="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          No Active Competitions
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Check back soon for exciting new competitions!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 px-safe">
      {enrolledCompetitions.length === 0 ? (
        <div className="text-center py-8">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-2xl shadow-lg mb-4">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Welcome to {organizationName}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              You haven't joined any competitions yet. Choose a competition below to get started!
            </p>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">
            My Competitions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {enrolledCompetitions.map((competition) => (
              <CompetitionDashboardCard
                key={competition.id}
                competition={competition}
                organizationSlug={organizationSlug}
                organizationName={organizationName}
                onNavigate={() => handleNavigate(competition)}
                onUpdateEntryNickname={handleUpdateEntryNickname}
              />
            ))}
          </div>
        </div>
      )}

      {availableCompetitions.length > 0 && (
        <div className={enrolledCompetitions.length > 0 ? "border-t border-gray-200 dark:border-gray-700 pt-8" : ""}>
          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {enrolledCompetitions.length === 0 ? 'Active Competitions' : 'Other Competitions'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {availableCompetitions.length} competition{availableCompetitions.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <div className={enrolledCompetitions.length === 0 ? "space-y-3" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"}>
            {availableCompetitions.map((competition) => {
              const stats = publicStats[competition.id];
              return (
                <PublicCompetitionCard
                  key={competition.id}
                  competition={competition}
                  onClick={() => handleNavigate(competition)}
                  totalEntrants={stats?.total ?? 0}
                  remainingEntrants={stats?.remaining ?? null}
                  wcFirstKickoff={wcFirstKickoff}
                  firstGameweekDeadline={firstGameweekDeadlines[competition.id] ?? null}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
